import React, { Component } from 'react';
import PropTypes from 'prop-types';
import lodashValues from 'lodash/fp/values';
import * as SCREEN_IDS from 'client/constants/screenIds';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';

import H1 from 'client/components/H1';
import SrBox from 'client/components/SrBox';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrCheckboxList from 'client/components/SrCheckboxList';
import SrDatePicker from 'client/components/SrDatePicker';
import TextArea from 'client/components/TextArea';
import InputText from 'client/components/InputText';
import SrMapDrawn from 'client/components/SrMapDrawn';
import SrFormikField from 'client/components/SrFormikField';

import isEmail from 'common/util/isEmail';

const screenIdList = lodashValues(SCREEN_IDS);

export const SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT = 'short';
export const SIGNUP_ONBOARDING_FLOW_VARIANT_LONG = 'long';
export const SIGNUP_ONBOARDING_FLOW_VARIANT_ALTERNATE = 'alternate';
export const SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT_V2 = 'shortV2';

@withScreenId((props) => {
  if (props.onboardingFlowVariant === SIGNUP_ONBOARDING_FLOW_VARIANT_SHORT) {
    return `Short ${props.screenId}`;
  }
  return props.screenId;
})
@withScrollToTop
class OnboardingStep extends Component {
  constructor(props) {
    super(props);

    this.state = this._initialState(props);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.id !== nextProps.id) {
      this.setState(this._initialState(nextProps));
    }
  }

  _initialState = (props) => {
    const _state = {
      ...props,
      isValid: this._isValid(props),
      value: props.value || undefined,
    };

    if (props.allowEmptyValue && !_state.value) {
      _state.value = [];
    } else if (!_state.value && props.value === 0) {
      _state.value = 0;
    }
    return _state;
  };

  isDate = (input) => {
    if (Object.prototype.toString.call(input) === '[object Date]') {
      return true;
    }
    return false;
  };

  _isValid = ({ type, value, multi, allowEmptyValue } = {}) => {
    if (allowEmptyValue) {
      return true;
    }

    switch (type) {
      case 'map-draw':
        return Array.isArray(value);
      case 'checkbox-list':
        return multi ? Array.isArray(value) && value.length > 0 : typeof value !== 'undefined';
      case 'datetime':
        return typeof value === 'object';
      case 'currency-range':
        return (
          Array.isArray(value) &&
          value &&
          value[0] &&
          !isNaN(Number(value[0])) &&
          value[1] &&
          !isNaN(Number(value[1])) &&
          value[0] <= value[1]
        );
      case 'date-range':
        return (
          Array.isArray(value) &&
          value.length === 2 &&
          this.isDate(value[0]) &&
          this.isDate(value[1])
        );
      case 'tel':
        return (
          value && value.length === 10 && !isNaN(Number(value)) && String(value).indexOf('.') === -1
        );
      case 'code':
        return value && value.length === 4 && !isNaN(Number(value));
      case 'textarea':
      case 'text':
      case 'password':
        return typeof value === 'string' && value.length > 0;
      case 'email':
        return isEmail(value);
    }
  };

  _onChange = (event, index) => {
    let isValid = false;
    let value = null;
    const { type, multi, allowEmptyValue, onboardingValues, id } = this.props;

    switch (type) {
      case 'map-draw':
        value = event;
        isValid = this._isValid({ type, value });
        break;
      case 'checkbox-list':
        value = event;
        isValid = this._isValid({
          type,
          value,
          multi,
          allowEmptyValue,
        });
        break;
      case 'datetime':
        isValid = this._isValid({ type, value: event });
        value = event && event.toDate ? event.toDate() : '';
        break;
      case 'date-range':
        value = new Array(2);
        if (onboardingValues[id]) {
          if (onboardingValues[id][0]) {
            value[0] = onboardingValues[id][0];
          }
          if (onboardingValues[id][1]) {
            value[1] = onboardingValues[id][1];
          }
        } else if (this.state.value) {
          value = this.state.value;
        }

        value[index] = event && event.toDate ? event.toDate() : '';
        isValid = this._isValid({ type, value });
        break;
      case 'currency-range':
        value = new Array(2);
        if (this.state.value) {
          if (this.state.value[0]) {
            value[0] = this.state.value[0];
          }
          if (this.state.value[1]) {
            value[1] = this.state.value[1];
          }
        } else if (onboardingValues[id]) {
          if (onboardingValues[id][0]) {
            value[0] = onboardingValues[id][0];
          }
          if (onboardingValues[id][1]) {
            value[1] = onboardingValues[id][1];
          }
        }
        value[index] = event && !isNaN(Number(event)) ? event : '';
        isValid = this._isValid({ type, value });
        break;
      case 'tel':
        value =
          event && !isNaN(Number(event.target.value)) && event.target.value.length <= 10 // value is a number and it has no more than 10 digits
            ? event.target.value
            : this.state.value;
        if (String(value).indexOf('.') > -1) {
          value = parseInt(String(value).replace('.', ''), 10);
        }
        isValid = this._isValid({ type, value });
        break;
      case 'code':
        value =
          event && !isNaN(Number(event.target.value)) && event.target.value.length <= 4 // value is a number and it has no more than 4 digits
            ? event.target.value
            : this.state.value;
        isValid = this._isValid({ type, value });
        break;
      case 'textarea':
      case 'text':
      case 'password':
        value = event.target.value;
        isValid = this._isValid({ type, value });
        break;
      case 'email':
        value = event.target.value.trim();
        isValid = this._isValid({ type, value });
        break;
    }

    this.setState({
      value,
      isValid,
    });
  };

  _onSubmitStep = () => {
    const { value } = this.state;
    const { id, onSubmit } = this.props;

    onSubmit(id, value);
  };

  render() {
    const {
      type,
      title,
      explainerText,
      placeholder,
      optionList,
      nextTitleButton,
      multi,
      onboardingValues,
      onboardingProps,
      id,
    } = this.props;

    const { isValid, value } = this.state;

    let formDom = null;

    switch (type) {
      case 'checkbox-list':
        formDom = (
          <SrCheckboxList
            optionList={
              typeof optionList === 'function'
                ? optionList(onboardingValues, onboardingProps)
                : optionList
            }
            onChange={this._onChange}
            value={value}
            multipleSelection={multi}
          />
        );
        break;
      case 'datetime':
        formDom = (
          <SrDatePicker
            className="choice datePicker"
            placeholder={placeholder}
            onChange={this._onChange}
            value={value}
            closeOnSelect
            name={id}
          />
        );
        break;
      case 'date-range':
        formDom = (
          <div>
            <SrDatePicker
              className="choice datePicker"
              placeholder="Earliest Date"
              isValidDate={(current) => {
                if (!current.isSameOrAfter(new Date())) {
                  return false;
                }
                if (value && value[1] && !current.isSameOrBefore(value[1])) {
                  return false;
                }
                return true;
              }}
              onChange={(e) => this._onChange(e, 0)}
              value={value && value[0]}
              closeOnSelect
            />
            <SrDatePicker
              className="choice datePicker"
              placeholder="Latest Date"
              isValidDate={(current) => {
                if (!current.isSameOrAfter(new Date())) {
                  return false;
                }
                if (value && value[0] && !current.isSameOrAfter(value[0])) {
                  return false;
                }
                return true;
              }}
              onChange={(e) => this._onChange(e, 1)}
              value={value && value[1]}
              closeOnSelect
            />
          </div>
        );
        break;
      case 'currency-range':
        formDom = (
          <div className="input-range">
            <SrFormikField
              formField={{
                id: 'min',
                title: '',
                properties: {
                  placeholder: 'min rent',
                  onChange: (e) => this._onChange(e, 0),
                },
                type: 'currency',
              }}
              value={value ? value[0] : null}
            />
            <SrFormikField
              formField={{
                id: 'max',
                properties: {
                  placeholder: 'max rent',
                  onChange: (e) => this._onChange(e, 1),
                },
                type: 'currency',
              }}
              value={value ? value[1] : null}
            />
          </div>
        );
        break;
      case 'textarea':
        formDom = (
          <TextArea
            className="choice"
            placeholder={placeholder}
            onChange={this._onChange}
            value={value || ''}
          />
        );
        break;
      case 'text':
      case 'email':
        formDom = (
          <InputText
            className="choice"
            placeholder={placeholder}
            onChange={this._onChange}
            type="text"
            value={value || ''}
          />
        );
        break;
      case 'tel':
        formDom = (
          <InputText
            className="choice"
            placeholder={placeholder}
            onChange={this._onChange}
            type="tel"
            pattern="[0-9]*"
            inputmode="numeric"
            value={value || ''}
          />
        );
        break;
      case 'code':
        formDom = (
          <InputText
            className="choice"
            placeholder={placeholder}
            onChange={this._onChange}
            type="tel"
            pattern="[0-9]*"
            inputmode="numeric"
            value={value || ''}
          />
        );
        break;
      case 'password':
        formDom = (
          <InputText
            className="choice"
            placeholder={placeholder}
            onChange={this._onChange}
            type="password"
            value={value || ''}
          />
        );
        break;
      case 'map-draw':
        formDom = (
          <SrMapDrawn onChange={this._onChange} value={value} market={onboardingProps.market} />
        );
        break;
    }

    return (
      <div className="stepContainer">
        <H1>{typeof title === 'function' ? title(onboardingValues) : title}</H1>
        <span className="explainerText">
          {typeof explainerText === 'function' ? explainerText(onboardingValues) : explainerText}
        </span>
        <div className="answers">{formDom}</div>
        <SrBox marginTop="10px">
          <ButtonDeprecated disabled={!isValid} onClick={this._onSubmitStep}>
            {nextTitleButton}
          </ButtonDeprecated>
        </SrBox>
      </div>
    );
  }
}

OnboardingStep.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  screenId: PropTypes.oneOf(screenIdList).isRequired, // eslint-disable-line react/no-unused-prop-types
  onboardingFlowVariant: PropTypes.string.isRequired, // eslint-disable-line react/no-unused-prop-types
  onboardingProps: PropTypes.object.isRequired,
  placeholder: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  optionList: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  nextTitleButton: PropTypes.string,
};

OnboardingStep.defaultProps = {
  placeholder: '',
  nextTitleButton: 'next',
};

export default OnboardingStep;
